import { CardPlaceholder } from '@/components/card';
import CollapsibleTable from '@/components/collapsible-table';
import { useSnackbar } from '@/contexts/SnackBarContext';
import { ForfeitureHistoryTableCell } from '@/routes/plans/plan-detail/PlanForfeituresTab/ForfeitureHistoryTableCell.component';
import { PlanService } from '@/services/Plan.service';
import DOMInteraction from '@/utils/DOMInteraction';
import { LoadingButton } from '@mui/lab';
import {
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { useMutation, useQuery } from '@tanstack/react-query';

import { capitalize } from 'lodash';
import React, { FC, useCallback, useMemo, useState } from 'react';

interface ForfeitureHistoryProps {
  sponsorPlanId: number;
}

const columns = [
  { field: 'createdAt', headerName: 'Date', width: 130 },
  { field: 'source', headerName: 'Source', width: 130 },
  { field: 'category', headerName: 'Description', width: 130 },
  { field: 'amount', headerName: 'Amount', width: 130 },
  { field: 'status', headerName: 'Status', width: 130 },
  { field: 'tracerId', headerName: 'Tracer ID', width: 260 }
];

export const ForfeitureHistory: FC<ForfeitureHistoryProps> = props => {
  const { showSnackbar } = useSnackbar();

  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [pageNumber, setPageNumber] = useState(1);
  const [accountType, setAccountType] = useState('forfeiture,suspense');

  const transactions = useQuery(
    ['PlanService.getSubaTransactions', props.sponsorPlanId, accountType],
    () =>
      PlanService.getSubaTransactions({
        accountType,
        pageNumber,
        pageSize: rowsPerPage,
        planId: props.sponsorPlanId
      }),
    {
      enabled: Boolean(props.sponsorPlanId),
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      select: data => {
        return data.map(item => ({
          ...item,
          source: capitalize(
            item.accountId?.slice(item.accountId?.indexOf(':') + 1)
          ),
          sponsorPlanId: props.sponsorPlanId
        }));
      }
    }
  );

  const downloadReport = useMutation(
    ['PlanService.getSubaTransactionsReport', props.sponsorPlanId, accountType],
    () =>
      PlanService.getSubaTransactionsReport(
        props.sponsorPlanId,
        accountType,
        'csv'
      ),
    {
      onError: () =>
        showSnackbar({
          message: 'Error downloading document',
          severity: 'error'
        }),
      onSuccess: data =>
        DOMInteraction.triggerDownload(data, 'Transactions History.csv')
    }
  );

  const onDownload = useCallback(() => downloadReport.mutateAsync(), []);

  const onAccountTypeChanged = useCallback((event, value) => {
    if (value) {
      setAccountType(value);
      setPageNumber(1);
    }
  }, []);

  const pager = useMemo(
    () => ({
      metaCount: transactions.data?.length,
      onPageNumberChanged: (zeroIndexedPageNumber: number) => {
        return setPageNumber(zeroIndexedPageNumber + 1);
      },
      onRowsPerPageChanged: (newRowsPerPage: number) => {
        return setRowsPerPage(newRowsPerPage);
      },
      pageNumber: pageNumber - 1,
      rowsPerPage
    }),
    [pageNumber, rowsPerPage, transactions.data?.length]
  );

  return (
    <Stack
      border={1}
      borderColor={grey[200]}
      borderRadius={1}
      data-testid='pendingTransactionsSection'>
      <Stack
        alignItems='center'
        borderBottom={1}
        borderColor={grey[200]}
        display='flex'
        flex={1}
        flexDirection='row'
        justifyContent='space-between'
        p={2}>
        <Stack>
          <Typography data-testid='pendingTransactionsTitle' variant='h5'>
            Transactions History
          </Typography>
        </Stack>
        <Stack display='flex' flexDirection='row' gap={2}>
          <ToggleButtonGroup
            color='primary'
            data-testid='pendingTransactionsFilter'
            exclusive
            onChange={onAccountTypeChanged}
            size='small'
            value={accountType}>
            <ToggleButton
              data-testid='pendingTransactionsFilterForfeitureAndSuspense'
              value='forfeiture,suspense'>
              All
            </ToggleButton>
            <ToggleButton
              data-testid='pendingTransactionsFilterForfeiture'
              value='forfeiture'>
              Forfeiture
            </ToggleButton>
            <ToggleButton
              data-testid='pendingTransactionsFilterSuspense'
              value='suspense'>
              Suspense
            </ToggleButton>
          </ToggleButtonGroup>
          <LoadingButton
            data-testid='downloadButton'
            loading={downloadReport.isLoading}
            onClick={onDownload}>
            Download
          </LoadingButton>
        </Stack>
      </Stack>
      <CollapsibleTable
        cellComponent={ForfeitureHistoryTableCell}
        columns={columns}
        data-testid='pendingTransactionsTable'
        isLoading={transactions.isLoading}
        noDataPlaceholderComponent={
          <CardPlaceholder
            data-testid='noDataPendingTransactionsTable'
            subtitle={`${
              transactions.isError ? 'Error' : 'No data for this plan'
            }`}
          />
        }
        pager={pager}
        rootPaperElevation={0}
        tableData={
          transactions.data?.slice(
            rowsPerPage * pageNumber - rowsPerPage,
            rowsPerPage * pageNumber
          ) || []
        }
        useDivAsBackground
      />
    </Stack>
  );
};
